const DARK_BACKGROUND = '#0f172a';

function normalizeHex(value, fallback) {
  if (typeof value !== 'string') return fallback;
  const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(value.trim());
  if (!match) return fallback;
  const digits = match[1].length === 3 ? [...match[1]].map(c => c + c).join('') : match[1];
  return `#${digits.toLowerCase()}`;
}

function rgb(hex) {
  return [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16));
}

function luminance(hex) {
  const channels = rgb(hex).map(channel => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function contrast(first, second) {
  const [lighter, darker] = [luminance(first), luminance(second)].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
}

function foreground(color) {
  return contrast(color, '#ffffff') >= contrast(color, '#000000') ? '#ffffff' : '#000000';
}

function lightenForDark(color) {
  if (contrast(color, DARK_BACKGROUND) >= 4.5) return color;
  const source = rgb(color);
  let low = 0;
  let high = 1;
  for (let step = 0; step < 12; step += 1) {
    const amount = (low + high) / 2;
    const mixed = `#${source
      .map(channel =>
        Math.round(channel + (255 - channel) * amount)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')}`;
    if (contrast(mixed, DARK_BACKGROUND) >= 4.5) high = amount;
    else low = amount;
  }
  return `#${source
    .map(channel =>
      Math.round(channel + (255 - channel) * high)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

function hslTriplet(hex) {
  const [red, green, blue] = rgb(hex).map(channel => channel / 255);
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;
  let saturation = 0;
  const lightness = (max + min) / 2;
  if (delta) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    switch (max) {
      case red:
        hue = ((green - blue) / delta) % 6;
        break;
      case green:
        hue = (blue - red) / delta + 2;
        break;
      default:
        hue = (red - green) / delta + 4;
    }
    hue *= 60;
  }
  const rounded = value => Math.round(value * 100) / 100;
  return `${rounded((hue + 360) % 360)} ${rounded(saturation * 100)}% ${rounded(lightness * 100)}%`;
}

export function adminPalette(primaryColor, secondaryColor) {
  const primary = normalizeHex(primaryColor, '#086cbf');
  const secondary = normalizeHex(secondaryColor, '#b51913');
  const darkPrimary =
    contrast(primary, DARK_BACKGROUND) >= 4.5
      ? primary
      : contrast(secondary, DARK_BACKGROUND) >= 4.5
        ? secondary
        : lightenForDark(primary);
  const darkSecondary = lightenForDark(secondary);

  return {
    $adminPrimary: hslTriplet(primary),
    $adminPrimaryForeground: hslTriplet(foreground(primary)),
    $adminSecondary: hslTriplet(secondary),
    $adminSecondaryForeground: hslTriplet(foreground(secondary)),
    $adminDarkPrimary: hslTriplet(darkPrimary),
    $adminDarkPrimaryForeground: hslTriplet(foreground(darkPrimary)),
    $adminDarkSecondary: hslTriplet(darkSecondary),
    $adminDarkSecondaryForeground: hslTriplet(foreground(darkSecondary)),
  };
}
